import React, { useState, useEffect } from "react";
import {
  MapPin,
  Navigation,
  Save,
  X,
  AlertCircle,
  CheckCircle2,
  Sliders,
} from "lucide-react";
import MapPreview from "./MapPreview";
import MapLocationSearchBar from "./MapLocationSearchBar";
import { getCurrentGPSLocation, saveSiteGeoFence } from "../../services/geofenceService";
import { showToast } from "../../utils/toast";

export default function EditSiteGeoFenceModal({
  isOpen,
  site,
  onClose,
  onSaveSuccess,
}) {
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radius, setRadius] = useState(100);
  const [enabled, setEnabled] = useState(true);

  const [isCapturingGPS, setIsCapturingGPS] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [capturedLocation, setCapturedLocation] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Sync state whenever modal opens or site changes
  useEffect(() => {
    if (isOpen && site) {
      const isSiteEnabled = site.geofence_enabled !== false;
      const isConfigured = Boolean(
        isSiteEnabled &&
        site.latitude !== null &&
        site.latitude !== undefined &&
        site.latitude !== "" &&
        site.longitude !== null &&
        site.longitude !== undefined &&
        site.longitude !== ""
      );
      setLatitude(
        isSiteEnabled && site.latitude !== null && site.latitude !== undefined ? String(site.latitude) : ""
      );
      setLongitude(
        isSiteEnabled && site.longitude !== null && site.longitude !== undefined ? String(site.longitude) : ""
      );
      setRadius(site.geofence_radius || 100);
      setEnabled(isSiteEnabled);
      setGpsAccuracy(null);
      setCapturedLocation(null);
      setErrorMessage("");
      setSuccessMessage("");
    }
  }, [isOpen, site]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen && !saving) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, saving, onClose]);

  if (!isOpen || !site) return null;

  // Handle "Use My Current Location"
  const handleUseCurrentLocation = async () => {
    setIsCapturingGPS(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const coords = await getCurrentGPSLocation();
      const latStr = coords.latitude.toFixed(8);
      const lonStr = coords.longitude.toFixed(8);

      setLatitude(latStr);
      setLongitude(lonStr);
      setGpsAccuracy(coords.accuracy);
      setCapturedLocation(coords);

      const msg = `Captured current GPS: ±${Math.round(coords.accuracy)}m accuracy. Review below and click "Save Changes".`;
      setSuccessMessage(msg);
      showToast(msg, "info");
    } catch (err) {
      console.error("GPS capture failed in modal:", err);
      const msg = err.userMessage || err.message || "Failed to capture current location.";
      setErrorMessage(msg);
      showToast(msg, "error");
    } finally {
      setIsCapturingGPS(false);
    }
  };

  // Handle place selection from search bar
  const handleSelectSearchedLocation = (place) => {
    if (!place) return;
    setLatitude(Number(place.latitude).toFixed(8));
    setLongitude(Number(place.longitude).toFixed(8));
    setCapturedLocation(null);
    setGpsAccuracy(null);
    setErrorMessage("");
    setSuccessMessage(`Selected "${place.name || place.displayName}". Coordinates updated!`);
  };

  // Handle direct map click coordinate adjustment
  const handleMapLocationSelect = (coords) => {
    if (!coords) return;
    setLatitude(Number(coords.latitude).toFixed(8));
    setLongitude(Number(coords.longitude).toFixed(8));
    setCapturedLocation(null);
    setGpsAccuracy(null);
    setErrorMessage("");
  };

  // Handle form submission / Save
  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    const latNum = Number(latitude);
    const lonNum = Number(longitude);
    const radNum = Number(radius);

    if (enabled) {
      if (latitude === "" || longitude === "") {
        setErrorMessage("Latitude and Longitude cannot be empty when Geo-Fencing is enabled.");
        return;
      }

      if (isNaN(latNum) || latNum < -90 || latNum > 90) {
        setErrorMessage("Please enter a valid Latitude between -90 and 90 degrees.");
        return;
      }

      if (isNaN(lonNum) || lonNum < -180 || lonNum > 180) {
        setErrorMessage("Please enter a valid Longitude between -180 and 180 degrees.");
        return;
      }
    }

    if (isNaN(radNum) || radNum <= 0) {
      setErrorMessage("Geofence Radius must be greater than 0 meters.");
      return;
    }

    if (radNum > 5000) {
      setErrorMessage("Geofence Radius cannot exceed 5,000 meters.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        latitude: enabled ? latNum : null,
        longitude: enabled ? lonNum : null,
        geofence_radius: radNum,
        geofence_enabled: enabled,
      };

      if (!isNaN(Number(site.id))) {
        await saveSiteGeoFence(site.id, payload);
      }

      showToast(
        enabled
          ? `Geo-fence for "${site.name}" updated successfully!`
          : `Geo-fence disabled for "${site.name}".`,
        "success"
      );

      if (onSaveSuccess) {
        onSaveSuccess({
          ...site,
          ...payload,
          is_geofence_configured: enabled && !!(payload.latitude && payload.longitude),
          status: enabled ? (payload.latitude && payload.longitude ? "GEOFENCE_ENABLED" : "LOCATION_NOT_CONFIGURED") : "GEOFENCE_DISABLED",
          updated_at: new Date().toISOString(),
        });
      }

      onClose();
    } catch (err) {
      console.error("Failed to save geofence from modal:", err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to save location configuration. Ensure you have Admin authorization.";
      setErrorMessage(detail);
      showToast(detail, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-geofence-modal-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl overflow-hidden my-8 animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 id="edit-geofence-modal-title" className="text-lg font-bold">
                Edit Geofence: {site.name}
              </h3>
              <p className="text-orange-100 text-xs">
                Update latitude, longitude coordinates and boundary radius.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition disabled:opacity-50"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 max-h-[calc(85vh-140px)] overflow-y-auto">
          {/* Quick Action: Use My Current Location */}
          <div className="bg-orange-50/70 border border-orange-200 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <span className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                <Navigation className="w-4 h-4 text-orange-600" />
                <span>Auto-fill from device GPS</span>
              </span>
              <p className="text-[11px] text-gray-600">
                Instantly populate latitude and longitude using your current physical location.
              </p>
            </div>
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={isCapturingGPS || saving}
              className="px-3.5 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 active:scale-95 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm transition shrink-0 disabled:opacity-60"
            >
              <Navigation className={`w-3.5 h-3.5 ${isCapturingGPS ? "animate-spin" : ""}`} />
              <span>{isCapturingGPS ? "Capturing..." : "Use My Current Location"}</span>
            </button>
          </div>

          {/* GPS Accuracy Pill */}
          {gpsAccuracy !== null && (
            <div className="p-2.5 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 text-xs flex items-center gap-2">
              <Navigation className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span>
                Captured GPS Accuracy: <b>±{Math.round(gpsAccuracy)} meters</b>
              </span>
            </div>
          )}

          {/* Coordinates Inputs: Latitude & Longitude */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Latitude */}
            <div className="space-y-1">
              <label htmlFor="modal-latitude" className="block text-xs font-bold text-gray-700">
                Latitude {enabled && <span className="text-red-500">*</span>}
              </label>
              <input
                id="modal-latitude"
                type="text"
                disabled={!enabled}
                value={enabled ? latitude : ""}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder={!enabled ? "Geo-fencing disabled" : "e.g. 19.178703"}
                className={`w-full px-3.5 py-2 rounded-xl border font-mono text-sm transition shadow-sm ${
                  !enabled
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                }`}
              />
              <p className="text-[10px] text-gray-500">{enabled ? "Value between -90.0 and 90.0" : "Disabled for this site"}</p>
            </div>

            {/* Longitude */}
            <div className="space-y-1">
              <label htmlFor="modal-longitude" className="block text-xs font-bold text-gray-700">
                Longitude {enabled && <span className="text-red-500">*</span>}
              </label>
              <input
                id="modal-longitude"
                type="text"
                disabled={!enabled}
                value={enabled ? longitude : ""}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder={!enabled ? "Geo-fencing disabled" : "e.g. 72.834726"}
                className={`w-full px-3.5 py-2 rounded-xl border font-mono text-sm transition shadow-sm ${
                  !enabled
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-orange-500 focus:outline-none"
                }`}
              />
              <p className="text-[10px] text-gray-500">{enabled ? "Value between -180.0 and 180.0" : "Disabled for this site"}</p>
            </div>
          </div>

          {/* Radius and Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Geofence Radius */}
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <label htmlFor="modal-radius" className="text-xs font-bold text-gray-700">
                  Radius (meters)
                </label>
                <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-200">
                  Default 100m
                </span>
              </div>
              <div className="relative">
                <input
                  id="modal-radius"
                  type="number"
                  min="10"
                  max="5000"
                  step="5"
                  value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-xl border border-gray-300 text-sm text-gray-900 font-medium focus:ring-2 focus:ring-orange-500 focus:outline-none transition shadow-sm"
                />
                <span className="absolute right-3.5 top-2 text-xs text-gray-400 font-medium">
                  m
                </span>
              </div>
            </div>

            {/* Geofence Status */}
            <div className="space-y-1">
              <label className="block text-xs font-bold text-gray-700">
                Enforcement Status
              </label>
              <div className="flex items-center gap-2 pt-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setEnabled(true);
                  }}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${
                    enabled
                      ? "bg-emerald-500 border-emerald-600 text-white shadow-sm"
                      : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEnabled(false);
                    setLatitude("");
                    setLongitude("");
                    setCapturedLocation(null);
                    setGpsAccuracy(null);
                  }}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${
                    !enabled
                      ? "bg-rose-500 border-rose-600 text-white shadow-sm"
                      : "bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Disabled
                </button>
              </div>
            </div>
          </div>

          {/* Disabled Notice Banner */}
          {!enabled && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>Geo-Fencing is <b>Disabled</b>. Active coordinates are cleared and site presence validation is bypassed. Click <b>"Save Changes"</b> to apply.</span>
            </div>
          )}

          {/* Map Preview inside Modal */}
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <span className="block text-xs font-bold text-gray-700">
                Search Place or Click Map to Set Coordinates
              </span>
            </div>
            <MapLocationSearchBar
              onSelectLocation={(place) => {
                handleSelectSearchedLocation(place);
                setEnabled(true);
              }}
              placeholder="Search place, landmark, or address..."
            />
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <MapPreview
                latitude={enabled ? latitude : null}
                longitude={enabled ? longitude : null}
                radius={radius}
                userLocation={enabled ? capturedLocation : null}
                siteName={site.name}
                height={220}
                onLocationSelect={enabled ? handleMapLocationSelect : null}
                disabled={!enabled}
              />
            </div>
          </div>

          {/* Feedback Messages */}
          {errorMessage && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-semibold text-xs transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || isCapturingGPS}
            className="px-5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-95 text-white font-bold text-xs flex items-center gap-1.5 shadow-md transition disabled:opacity-60"
          >
            <Save className={`w-3.5 h-3.5 ${saving ? "animate-spin" : ""}`} />
            <span>{saving ? "Saving Changes..." : "Save Changes"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
